import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2,
  Users,
  Star,
  Clock,
  DollarSign,
  BookOpen,
  Globe,
  EyeOff,
  PlayCircle
} from 'lucide-react';
import { coursesApi, categoriesApi } from '@/services/api';
import { Course, Category } from '@/types';
import { toast } from 'sonner';
import { ModulesManager } from '@/components/ModulesManager';

const courseSchema = z.object({
  title: z.string().min(3, 'El título debe tener al menos 3 caracteres'),
  description: z.string().min(10, 'La descripción debe tener al menos 10 caracteres'),
  categoryId: z.string().min(1, 'Selecciona una categoría'),
  price: z.number().min(0, 'El precio debe ser mayor o igual a 0'),
  hasDiscount: z.boolean().default(false),
  discountPercentage: z.number().min(0).max(100).optional(),
  originalPrice: z.number().min(0).optional(),
  level: z.enum(['Principiante', 'Intermedio', 'Avanzado']).optional(),
  duration: z.number().optional(),
  thumbnail: z.string().optional(),
  trailer: z.string().optional(),
});

type CourseFormData = z.infer<typeof courseSchema>;

export const CoursesPage: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCourseForModules, setSelectedCourseForModules] = useState<Course | null>(null);
  const [isModulesModalOpen, setIsModulesModalOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CourseFormData>({
    resolver: zodResolver(courseSchema),
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [coursesResponse, categoriesResponse] = await Promise.all([
        coursesApi.getAll(),
        categoriesApi.getAll(),
      ]);
      
      // Handle courses like StudentsPage does
      if ((coursesResponse.data as any).success) {
        setCourses((coursesResponse.data as any).data?.courses || (coursesResponse.data as any).data || []);
      } else {
        setCourses([]);
      }
      
      // Handle categories like StudentsPage does  
      if ((categoriesResponse.data as any).success) {
        setCategories((categoriesResponse.data as any).data || []);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar los datos');
      setCourses([]);
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: CourseFormData) => {
    try {
      if (editingCourse) {
        await coursesApi.update(editingCourse.id, data);
        toast.success('Curso actualizado exitosamente');
      } else {
        await coursesApi.create(data);
        toast.success('Curso creado exitosamente');
      }
      
      setIsModalOpen(false);
      setEditingCourse(null);
      reset();
      
      // Refrescar la lista desde el servidor
      await fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al guardar el curso');
    }
  };

  const handleEdit = (course: Course) => {
    setEditingCourse(course);
    reset({
      title: course.title,
      description: course.description,
      categoryId: course.category.id,
      price: course.price,
      hasDiscount: course.hasDiscount || false,
      discountPercentage: course.discountPercentage || 0,
      originalPrice: course.originalPrice || undefined,
      level: course.level as any,
      duration: course.duration || undefined,
      thumbnail: course.thumbnail || undefined,
      trailer: course.trailer || undefined,
    });
    setIsModalOpen(true);
  };

  const handleManageModules = (course: Course) => {
    setSelectedCourseForModules(course);
    setIsModulesModalOpen(true);
  };

  const handleDelete = async (course: Course) => {
    if (!confirm(`¿Estás seguro de eliminar el curso "${course.title}"?`)) return;
    
    try {
      await coursesApi.delete(course.id);
      toast.success('Curso eliminado exitosamente');
      // Refrescar la lista desde el servidor
      await fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al eliminar el curso');
    }
  };

  const handlePublish = async (course: Course) => {
    const action = course.status === 'PUBLISHED' ? 'despublicar' : 'publicar';
    if (!confirm(`¿Estás seguro de ${action} el curso "${course.title}"?`)) return;
    
    try {
      await coursesApi.updateStatus(course.id, course.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED');
      toast.success(`Curso ${action === 'publicar' ? 'publicado' : 'despublicado'} exitosamente`);
      // Refrescar la lista desde el servidor
      await fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || `Error al ${action} el curso`);
    }
  };

  const openCreateModal = () => {
    setEditingCourse(null);
    reset();
    setIsModalOpen(true);
  };

  const filteredCourses = (courses || []).filter(course => {
    if (!course) return false;
    
    const matchesSearch = (course.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (course.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || (course.category && course.category.id === selectedCategory);
    return matchesSearch && matchesCategory;
  });

  const getStatusBadge = (status: string) => {
    const styles = {
      PUBLISHED: 'bg-green-100 text-green-800',
      DRAFT: 'bg-yellow-100 text-yellow-800',
      ARCHIVED: 'bg-gray-100 text-gray-800',
    };
    
    const labels = {
      PUBLISHED: 'Publicado',
      DRAFT: 'Borrador',
      ARCHIVED: 'Archivado',
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status as keyof typeof styles]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
          <div className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
        </div>
        <div className="grid gap-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <div className="h-32 bg-gray-200 rounded"></div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Cursos</h1>
          <p className="text-gray-600">Administra todos los cursos de la plataforma</p>
        </div>
        <Button onClick={openCreateModal} leftIcon={Plus}>
          Nuevo Curso
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Buscar cursos..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="md:w-48">
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">Todas las categorías</option>
              {(categories || []).map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Courses Grid */}
      <div className="grid gap-6">
        {(filteredCourses || []).map((course) => (
          <Card key={course.id}>
            <div className="flex items-start space-x-4">
              {/* Course Thumbnail */}
              <div className="w-20 h-20 rounded-lg flex items-center justify-center text-white text-xl font-bold flex-shrink-0 overflow-hidden">
                {course.thumbnail ? (
                  <img 
                    src={course.thumbnail} 
                    alt={course.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Si falla la carga de la imagen, mostrar el fallback
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      target.parentElement!.style.backgroundColor = course.category.color || '#3B82F6';
                      target.parentElement!.textContent = course.title.charAt(0);
                    }}
                  />
                ) : (
                  <div 
                    className="w-full h-full flex items-center justify-center"
                    style={{ backgroundColor: course.category.color || '#3B82F6' }}
                  >
                    {course.title.charAt(0)}
                  </div>
                )}
              </div>

              {/* Course Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {course.title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                      {course.description}
                    </p>
                    <div className="flex items-center space-x-4 mt-3">
                      <span className="inline-flex items-center text-sm text-gray-600">
                        <span 
                          className="w-2 h-2 rounded-full mr-2"
                          style={{ backgroundColor: course.category.color || '#3B82F6' }}
                        ></span>
                        {course.category.name}
                      </span>
                      <span className="text-sm text-gray-600">
                        {course.instructor.firstName} {course.instructor.lastName}
                      </span>
                      {getStatusBadge(course.status)}
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center space-x-6 mt-4 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Users className="h-4 w-4 mr-1" />
                    {course.studentsCount} estudiantes
                  </div>
                  <div className="flex items-center">
                    <Star className="h-4 w-4 mr-1" />
                    {course.rating || 0} rating
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {course.duration ? `${Math.floor(course.duration / 60)}h ${course.duration % 60}m` : 'N/A'}
                  </div>
                  <div className="flex items-center">
                    <DollarSign className="h-4 w-4 mr-1" />
                    {course.hasDiscount ? (
                      <div className="flex items-center space-x-2">
                        <span className="text-green-600 font-semibold">
                          ${course.price}
                        </span>
                        <span className="text-gray-400 line-through text-sm">
                          ${course.originalPrice}
                        </span>
                        <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full font-medium">
                          -{course.discountPercentage}%
                        </span>
                      </div>
                    ) : (
                      <span>${course.price}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-1 flex-wrap">
                <Button variant="ghost" size="sm" title="Ver curso">
                  <Eye className="h-4 w-4" />
                  <span className="ml-1 text-xs">Ver</span>
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleManageModules(course)} 
                  title="Gestionar módulos"
                  className="text-purple-600 hover:text-purple-700 font-semibold"
                >
                  <PlayCircle className="h-4 w-4" />
                  <span className="ml-1 text-xs">Módulos</span>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleEdit(course)} title="Editar curso">
                  <Edit className="h-4 w-4" />
                  <span className="ml-1 text-xs">Editar</span>
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handlePublish(course)}
                  title={course.status === 'PUBLISHED' ? 'Despublicar curso' : 'Publicar curso'}
                  className={`${course.status === 'PUBLISHED' ? 'text-green-600 hover:text-green-700' : 'text-blue-600 hover:text-blue-700'} font-semibold`}
                >
                  {course.status === 'PUBLISHED' ? <EyeOff className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
                  <span className="ml-1 text-xs">
                    {course.status === 'PUBLISHED' ? 'Ocultar' : 'Publicar'}
                  </span>
                </Button>
                <Button variant="danger" size="sm" onClick={() => handleDelete(course)} title="Eliminar curso">
                  <Trash2 className="h-4 w-4" />
                  <span className="ml-1 text-xs">Eliminar</span>
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {filteredCourses.length === 0 && (
        <Card className="text-center py-12">
          <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No se encontraron cursos
          </h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || selectedCategory 
              ? 'Intenta ajustar los filtros de búsqueda'
              : 'Comienza creando tu primer curso'
            }
          </p>
          {!searchTerm && !selectedCategory && (
            <Button onClick={openCreateModal} leftIcon={Plus}>
              Crear Curso
            </Button>
          )}
        </Card>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {editingCourse ? 'Editar Curso' : 'Crear Nuevo Curso'}
              </h2>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <Input
                  {...register('title')}
                  label="Título del curso"
                  placeholder="Ej: Automatización con n8n"
                  error={errors.title?.message}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Describe el contenido del curso..."
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoría
                  </label>
                  <select
                    {...register('categoryId')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Seleccionar categoría</option>
                    {(categories || []).map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {errors.categoryId && (
                    <p className="mt-1 text-sm text-red-600">{errors.categoryId.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    {...register('price', { valueAsNumber: true })}
                    type="number"
                    min="0"
                    step="0.01"
                    label="Precio ($)"
                    placeholder="0.00"
                    error={errors.price?.message}
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nivel
                    </label>
                    <select
                      {...register('level')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Seleccionar nivel</option>
                      <option value="Principiante">Principiante</option>
                      <option value="Intermedio">Intermedio</option>
                      <option value="Avanzado">Avanzado</option>
                    </select>
                  </div>
                </div>

                {/* Promoción */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    Configuración de Promoción
                  </h3>

                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        {...register('hasDiscount')}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-sm text-gray-700">
                        Habilitar promoción/descuento
                      </label>
                    </div>

                    {watch('hasDiscount') && (
                      <div className="grid grid-cols-2 gap-3 ml-6">
                        <Input
                          {...register('originalPrice', { valueAsNumber: true })}
                          type="number"
                          min="0"
                          step="0.01"
                          label="Precio Original ($)"
                          placeholder="0.00"
                          error={errors.originalPrice?.message}
                        />
                        <Input
                          {...register('discountPercentage', { valueAsNumber: true })}
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          label="% Descuento"
                          placeholder="50"
                          error={errors.discountPercentage?.message}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <Input
                  {...register('duration', { valueAsNumber: true })}
                  type="number"
                  min="0"
                  label="Duración (minutos)"
                  placeholder="120"
                  error={errors.duration?.message}
                />

                <Input
                  {...register('thumbnail')}
                  type="url"
                  label="URL de Imagen (Thumbnail)"
                  placeholder="https://ejemplo.com/imagen.jpg"
                  error={errors.thumbnail?.message}
                />

                <Input
                  {...register('trailer')}
                  type="url"
                  label="URL de Video (Trailer)"
                  placeholder="https://ejemplo.com/video.mp4"
                  error={errors.trailer?.message}
                />

                <div className="flex space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex-1"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    isLoading={isSubmitting}
                  >
                    {editingCourse ? 'Actualizar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modules Manager Modal */}
      {selectedCourseForModules && (
        <ModulesManager
          course={selectedCourseForModules}
          isOpen={isModulesModalOpen}
          onClose={() => {
            setIsModulesModalOpen(false);
            setSelectedCourseForModules(null);
          }}
        />
      )}
    </div>
  );
};